/**
 * InviteAccept — /invite/:token (public)
 *
 * Recipient of an admin invite lands here. We verify the token, then
 * show either a signup form (new account will be created at the invited
 * role) or a "You already have an account — click accept" button (their
 * existing account will be promoted).
 *
 * On success we auto-log them in with the returned JWT and drop them
 * onto the appropriate landing page.
 */
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { AuthContext } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  ShieldCheck, Loader2, XCircle, Mail, Lock, User as UserIcon, Sparkles,
} from 'lucide-react';

const InviteAccept = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);
  const { API, login } = auth;

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState(null);

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [ageOk, setAgeOk] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await axios.get(`${API}/admin/invites/verify/${token}`);
        if (cancelled) return;
        if (!r.data.valid) {
          const reasonMsg = {
            not_found:        'That invite link is invalid or has been retracted.',
            revoked:          'This invite was revoked by an admin.',
            already_accepted: 'This invite has already been used.',
            expired:          'This invite has expired.',
          }[r.data.reason] || 'This invite link is no longer valid.';
          setError(reasonMsg);
        } else {
          setInvite(r.data);
        }
      } catch (e) {
        setError('Could not verify the invite. Try again or ask for a new link.');
      } finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [API, token]);

  const acceptExisting = async () => {
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/invites/accept`, { token });
      finishLogin(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not accept the invite');
    } finally { setSubmitting(false); }
  };

  const acceptNew = async (e) => {
    e && e.preventDefault();
    if (password.length < 6) { toast.error('Password must be at least 6 characters'); return; }
    if (password !== confirm) { toast.error('Passwords do not match'); return; }
    if (!ageOk) { toast.error('Please confirm you are 18 or older'); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/admin/invites/accept`, {
        token,
        password,
        display_name: displayName.trim() || null,
        age_confirmed: true,
      });
      finishLogin(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not create your account');
    } finally { setSubmitting(false); }
  };

  const finishLogin = (data) => {
    if (data.access_token) {
      login(data.access_token, {
        id:     data.user_id,
        email:  data.email,
        role:   data.role,
        status: 'active',
      });
    }
    toast.success(
      data.existing_user
        ? `Welcome back — your account is now ${data.role}.`
        : `Welcome! Your ${data.role} account is ready.`,
    );
    navigate(data.role === 'admin' ? '/admin/portal' : '/dashboard', { replace: true });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6 text-muted-foreground">
        <Loader2 className="w-5 h-5 mr-2 animate-spin" /> Verifying invite…
      </div>
    );
  }
  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center p-6">
        <div className="max-w-md w-full pro-card p-6 text-center" data-testid="invite-error">
          <div className="h-12 w-12 mx-auto rounded-2xl bg-rose-500/15 text-rose-600 flex items-center justify-center">
            <XCircle className="w-6 h-6" />
          </div>
          <h1 className="mt-3 font-heading text-xl font-extrabold">This invite can't be used</h1>
          <p className="mt-1 text-sm text-muted-foreground">{error}</p>
          <div className="mt-4">
            <Button onClick={() => navigate('/')} className="rounded-full font-bold">Go home</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-6">
      <div className="max-w-md w-full pro-card p-6" data-testid="invite-accept">
        <div className="flex items-start gap-3">
          <div className="h-11 w-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center flex-shrink-0">
            <ShieldCheck className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] uppercase tracking-widest font-bold text-primary">You&rsquo;re invited</div>
            <h1 className="font-heading text-xl font-extrabold">
              Join Freeze Dry Academy as {invite.role === 'admin' ? 'an admin' : `a ${invite.role} member`}
            </h1>
            <p className="mt-1 text-xs text-muted-foreground">
              Invited by <span className="font-semibold text-foreground">{invite.invited_by}</span>
              {invite.note ? <> · &ldquo;{invite.note}&rdquo;</> : null}
            </p>
          </div>
        </div>

        <div className="mt-4 rounded-lg border border-border bg-muted/30 px-3 py-2 text-xs">
          <span className="font-semibold text-foreground">Invited email:</span> {invite.email}
        </div>

        {invite.existing_user ? (
          <div className="mt-5">
            <div className="text-sm text-muted-foreground leading-relaxed">
              You already have an account with this email. Click below and we&rsquo;ll
              promote it to <strong>{invite.role}</strong> and sign you in.
            </div>
            <Button
              onClick={acceptExisting}
              disabled={submitting}
              className="mt-4 w-full rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="invite-accept-existing-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Promoting…</> : <><Sparkles className="w-4 h-4 mr-2" /> Accept &amp; sign in</>}
            </Button>
          </div>
        ) : (
          <form onSubmit={acceptNew} className="mt-5 space-y-3">
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Display name</label>
              <div className="relative mt-1">
                <UserIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Your name"
                  className="pl-9"
                  data-testid="invite-display-name"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Email</label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input value={invite.email} disabled className="pl-9 opacity-70" />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Password</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  className="pl-9" required minLength={6}
                  data-testid="invite-password"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest font-bold text-muted-foreground">Confirm password</label>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                  className="pl-9" required minLength={6}
                  data-testid="invite-password-confirm"
                />
              </div>
            </div>
            <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer pt-1">
              <input
                type="checkbox" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)}
                className="mt-0.5 h-4 w-4"
                data-testid="invite-age-confirm"
              />
              <span>
                I confirm I am <strong>18 years of age or older</strong> and I accept the community guidelines.
              </span>
            </label>
            <Button
              type="submit" disabled={submitting || !ageOk}
              className="w-full rounded-full font-bold bg-primary text-primary-foreground hover:bg-primary/90"
              data-testid="invite-accept-new-btn"
            >
              {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Creating account…</> : <><Sparkles className="w-4 h-4 mr-2" /> Create account &amp; sign in</>}
            </Button>
          </form>
        )}
      </div>
    </div>
  );
};

export default InviteAccept;
